/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from "react";

import { useDispatch, useSelector } from "react-redux";

import { useParams } from "react-router-dom";

import { Box, Stack, Typography, Modal, CircularProgress } from "@mui/material";

import moment from "moment";

import { fetchSoldQCDetails, fetchSummary } from "Pages/VehicleSummary/action";

import Gallery from "Components/Gallery/Gallery";
import Breadcrumb from "Components/UI/BreadCrumb/Breadcrumb";
import Container from "Components/UI/Container";

import accessTime from "Images/access_time.svg";
import personOutline from "Images/person_outline.svg";

import useQuery from "hooks/useQuery";

import VehicleInfo from "../VehicleInfoCard";

export default function SoldQCSection() {
  const { leadId } = useParams();
  const query = useQuery();
  const appointmentId = query.get("appointmentId");
  const dispatch = useDispatch();
  const { soldQCDetails, data, loading } = useSelector((state) => state.vehicleSummary);
  const [showSlideShowImages, setShowSlideShowImages] = useState(false);

  const breadcrumbs = [
    {
      link: "/home",
      title: "Home"
    },
    {
      link: `/vehicle-summary/${leadId}/vehicle-details`,
      title: "Vehicle Details"
    },
    {
      link: `#`,
      title: "Sold QC Images"
    }
  ];

  useEffect(() => {
    if (leadId && !data?.vehicleInfo) {
      dispatch(fetchSummary(leadId));
    }
    if (appointmentId && !soldQCDetails?.data) {
      dispatch(fetchSoldQCDetails(appointmentId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId, appointmentId]);

  const imageList = soldQCDetails?.data
    ? soldQCDetails?.data?.reduce?.((acc, curr) => {
        if (curr?.response?.WQ4?.images?.length > 0) {
          acc.push(...curr.response.WQ4.images);
        }
        return acc;
      }, [])
    : [];

  const latestQC = soldQCDetails?.data?.find?.((qc) => qc?.latest);

  return (
    <Stack direction="column" width="100%" minHeight="100vh" gap={2} bgcolor="#F0F3F5" mb="2rem">
      <Stack width="100%">
        <Box>
          <Container>
            <Breadcrumb breadcrumbs={breadcrumbs} />
          </Container>
        </Box>
      </Stack>
      {loading ? (
        <Container>
          <Stack minHeight="50vh" alignItems="center" justifyContent="center">
            <CircularProgress />
          </Stack>
        </Container>
      ) : (
        <>
          {/* Comments */}
          <Container>
            <Typography fontSize={["2.4rem"]} fontWeight={600} mb="2.4rem">
              Sold QC Images
            </Typography>
          </Container>
          <Container>
            <Stack bgcolor="#fff" borderRadius={2}>
              <VehicleInfo hideActions sx={{ px: 0 }} />
            </Stack>
          </Container>
          <Container>
            <Stack
              borderRadius={2}
              padding={4}
              bgcolor="#ffffff"
              width="100%"
              direction="row"
              mt="32px"
              justifyContent="space-between"
            >
              <Stack width="100%">
                <Typography fontSize={["2.4rem"]} fontWeight={600}>
                  Comments
                </Typography>
                {soldQCDetails?.data?.map?.((qc) => (
                  <Stack direction="column" mt="3.2rem" rowGap="1.2rem">
                    <Stack direction="row" justifyContent="space-between">
                      <Stack direction="row" alignItems="center">
                        <img src={personOutline} alt="person icon" />
                        <Typography fontSize={["1.4rem"]} fontWeight={600} color="#333333">
                          {qc?.completedBy?.uid}
                        </Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" columnGap="0.8rem">
                        <img
                          src={accessTime}
                          alt="time icon"
                          style={{ width: "20px", height: "20px", display: "inline-block" }}
                        />
                        <Typography fontSize={["1.4rem"]} fontWeight={600} color="#333333">
                          {moment.utc(qc?.completedAt).local().format("DD.MM.YYYY")}
                          <span style={{ marginLeft: "8px" }}>
                            {moment.utc(qc?.completedAt).local().format("hh:mm A")}
                          </span>
                        </Typography>
                      </Stack>
                    </Stack>
                    <Stack>
                      <Typography fontSize={["1.4rem"]} fontWeight={300} color="#333333">
                        {qc?.response?.WQ5?.response}
                      </Typography>
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </Container>
          {/* Comments end */}
          {/* DPF Section */}
          {(() => {
            const response = latestQC?.response || {};
            const dpfQuestions = ["WQ32", "WQ33", "WQ34"].filter((key) => response[key]).map((key) => response[key]);
            if (dpfQuestions.length === 0) return null;
            return (
              <Container>
                <Stack
                  borderRadius={2}
                  padding={4}
                  bgcolor="#ffffff"
                  width="100%"
                  direction="column"
                  mt="32px"
                  rowGap="2.4rem"
                >
                  <Typography fontSize={["2.4rem"]} fontWeight={600}>
                    DPF Section
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" columnGap="4.8rem" rowGap="2.4rem">
                    {dpfQuestions.map((question) => (
                      <Stack key={question.questionId} minWidth="200px">
                        <Typography fontSize={["1.2rem"]} fontWeight={400} color="#999999">
                          {question.title}
                        </Typography>
                        {question.responseFormat === "DOCUMENT" ? (
                          question.document?.path ? (
                            <a
                              href={question.document.path}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ fontSize: "1.4rem", fontWeight: 600, color: "#1890ff" }}
                            >
                              {question.document.name || "View Document"}
                            </a>
                          ) : (
                            <Typography fontSize={["1.4rem"]} fontWeight={600} color="#333333">
                              -
                            </Typography>
                          )
                        ) : question.responseFormat === "DROP_DOWN" ? (
                          <Typography fontSize={["1.4rem"]} fontWeight={600} color="#333333">
                            {question.responseOptions?.find((opt) => opt.selected)?.title || "-"}
                          </Typography>
                        ) : (
                          <Typography fontSize={["1.4rem"]} fontWeight={600} color="#333333">
                            {question.response || "-"}
                          </Typography>
                        )}
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              </Container>
            );
          })()}
          {/* DPF Section end */}
          {/* Battery Details */}
          {(() => {
            const response = latestQC?.response || {};
            const batteryQuestions = ["WQ35", "WQ36", "WQ37", "WQ38"]
              .filter((key) => response[key])
              .map((key) => response[key]);
            if (batteryQuestions.length === 0) return null;
            return (
              <Container>
                <Stack
                  borderRadius={2}
                  padding={4}
                  bgcolor="#ffffff"
                  width="100%"
                  direction="column"
                  mt="32px"
                  rowGap="2.4rem"
                >
                  <Typography fontSize={["2.4rem"]} fontWeight={600}>
                    Battery Details
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" columnGap="4.8rem" rowGap="2.4rem">
                    {batteryQuestions.map((question) => (
                      <Stack key={question.questionId} minWidth="200px">
                        <Typography fontSize={["1.2rem"]} fontWeight={400} color="#999999">
                          {question.title}
                        </Typography>
                        {question.responseFormat === "DOCUMENT" ? (
                          question.document?.path ? (
                            <a
                              href={question.document.path}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ fontSize: "1.4rem", fontWeight: 600, color: "#1890ff" }}
                            >
                              {question.document.name || "View Document"}
                            </a>
                          ) : (
                            <Typography fontSize={["1.4rem"]} fontWeight={600} color="#333333">
                              -
                            </Typography>
                          )
                        ) : question.responseFormat === "DROP_DOWN" ? (
                          <Typography fontSize={["1.4rem"]} fontWeight={600} color="#333333">
                            {question.responseOptions?.find((opt) => opt.selected)?.title || "-"}
                          </Typography>
                        ) : (
                          <Typography fontSize={["1.4rem"]} fontWeight={600} color="#333333">
                            {question.response || "-"}
                          </Typography>
                        )}
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              </Container>
            );
          })()}
          {/* Battery Details end */}
          {imageList?.length > 0 && (
            <Container>
              <Stack
                borderRadius={2}
                padding={4}
                bgcolor="#ffffff"
                width="100%"
                direction="column"
                mt="32px"
                mb="48px"
                justifyContent="space-between"
                rowGap="3.2rem"
              >
                <Typography fontSize={["2.4rem"]} fontWeight={600}>
                  Images
                </Typography>
                <Stack direction="row" alignItems="center" columnGap="2.4rem">
                  {imageList?.map((image) => (
                    <Box onClick={() => setShowSlideShowImages(true)}>
                      <img
                        src={image.path}
                        alt="qcImage"
                        width={68}
                        height={68}
                        style={{ borderRadius: "4px", cursor: "pointer" }}
                      />
                    </Box>
                  ))}
                </Stack>
              </Stack>
            </Container>
          )}
        </>
      )}
      {imageList?.length > 0 && (
        <Modal
          open={showSlideShowImages}
          onClose={() => setShowSlideShowImages(false)}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Gallery onClose={() => setShowSlideShowImages(false)} images={imageList} hideDownloadImage />
        </Modal>
      )}
    </Stack>
  );
}
